import { devtools, persist } from 'zustand/middleware'
import { shallow } from 'zustand/shallow'
import { createWithEqualityFn } from 'zustand/traditional'

// Make key a const so we can import it in other files
export const APP_STORE_KEY = '@bitlyft/app'

type AppState = {
	isSidebarOpen: boolean
}

type AppActions = {
	toggleSidebar: () => void
	reset: () => void
}

type AppStore = AppState & {
	actions: AppActions
}

const initialState: AppState = {
	isSidebarOpen: true,
}

const appStore = createWithEqualityFn<AppStore>()(
	devtools(
		persist(
			(set) => ({
				// Set initial state
				...initialState,

				actions: {
					/**
					 * We pass two more arguments to set so we can have a better dev experience and inspect with Redux DevTools
					 * @see: https://github.com/pmndrs/zustand?tab=readme-ov-file#redux-devtools
					 */
					toggleSidebar: () =>
						set(
							(state) => ({
								...state,
								isSidebarOpen: !state.isSidebarOpen,
							}),
							false,
							'app/toggleSidebar',
						),

					// Reset state to initial state
					reset: () => set(initialState, false, 'app/reset'),
				},
			}),
			// Name of the store in localStorage.
			// A `theme` key may still sit in an existing persisted blob from before
			// the app went dark-only (ADR 0014) — partialize drops it on the next
			// write and nothing reads it, so it is inert rather than migrated.
			{
				name: APP_STORE_KEY,
				partialize: ({ isSidebarOpen }) => ({
					isSidebarOpen,
				}),
			},
		),
		// Name of the store in Redux DevTools
		{ name: APP_STORE_KEY },
	),
	shallow,
)

export const useAppSidebar = () => appStore((state) => state.isSidebarOpen)
export const useAppActions = () => appStore((state) => state.actions)
